import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Icon from '../../ui/Icon';
import { Toggle, PageSkeleton } from '../../ui/primitives';
import { useAdminToast } from '../AdminShell';
import { STOCK_KEYS, StockConfig, StockTagLabels, saveStockConfig, stockErrorText, useStockConfig } from '../../magazyn/api';
import { TagPreview, useInvalidateStockConfig } from './shared';

/*
 * Ustawienia → Magazyn. Jak „Panel pakowania": każda zmiana zapisuje się od razu
 * (przełączniki/wybory), pola tekstowe i liczbowe — po wyjściu z pola albo Enterze.
 */

const MAX_STEPS = 5;
const MAX_REASONS = 12;
const MAX_REASON_LEN = 60;

/** 00:00, 00:30 … 23:30; 00:00 = północ, opisywana jako 24:00 (domyślna). */
const RESET_TIMES: { value: string; label: string }[] = Array.from({ length: 48 }, (_, i) => {
  const value = `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`;
  return { value, label: value === '00:00' ? '24:00 (północ)' : value };
});

/* ---------- wybór jednej z opcji (karty) ---------- */
function Choice<T extends string>({
  value, options, onChange, disabled,
}: {
  value: T;
  options: { id: T; title: string; desc: string; icon: string }[];
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="mga-choices" role="radiogroup">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          disabled={disabled}
          className={`mga-choice ${value === o.id ? 'on' : ''}`}
          onClick={() => value !== o.id && onChange(o.id)}
        >
          <span className="mga-choice-ic"><Icon name={o.icon} size={17} /></span>
          <span className="mga-choice-txt">
            <span className="t">{o.title}</span>
            <span className="d">{o.desc}</span>
          </span>
          <span className="mga-radio" aria-hidden />
        </button>
      ))}
    </div>
  );
}

/* ---------- edytor listy (żetony z × i pole do dopisania) ---------- */
function ChipListEditor({
  values, onChange, max, placeholder, validate, mono, render,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  max: number;
  placeholder: string;
  /** Zwraca znormalizowaną wartość albo komunikat błędu. */
  validate: (raw: string, current: string[]) => { value: string } | { error: string };
  mono?: boolean;
  render?: (v: string) => React.ReactNode;
}) {
  const push = useAdminToast();
  const [draft, setDraft] = useState('');
  const full = values.length >= max;

  function add() {
    const raw = draft.trim();
    if (!raw) return;
    const r = validate(raw, values);
    if ('error' in r) { push(r.error, 'err'); return; }
    setDraft('');
    onChange(values.concat(r.value));
  }

  return (
    <div className="mga-chipedit">
      {values.map((v, i) => (
        <span key={`${v}-${i}`} className={`mga-echip ${mono ? 'num' : ''}`}>
          {render ? render(v) : v}
          <button type="button" aria-label={`Usuń ${v}`} title="Usuń" onClick={() => onChange(values.filter((_, j) => j !== i))}>
            <Icon name="x" size={13} />
          </button>
        </span>
      ))}
      {!full && (
        <span className="mga-eadd">
          <input
            className="input"
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            inputMode={mono ? 'numeric' : undefined}
            style={{ width: mono ? 96 : 200 }}
          />
          <button type="button" className="btn btn-ghost btn-sm" onClick={add} disabled={!draft.trim()}><Icon name="plus" size={14} /> Dodaj</button>
        </span>
      )}
      {full && <span className="muted" style={{ fontSize: 12 }}>Limit: {max}</span>}
    </div>
  );
}

function Section({ icon, title, sub, children, dim }: { icon: string; title: string; sub: string; children: React.ReactNode; dim?: boolean }) {
  return (
    <div className={`card mga-sec ${dim ? 'dim' : ''}`}>
      <div className="card-head">
        <div className="mga-sec-head">
          <span className="mga-sec-ic"><Icon name={icon} size={17} /></span>
          <div><h3>{title}</h3><div className="ch-sub">{sub}</div></div>
        </div>
      </div>
      <div className="card-pad" style={{ paddingTop: 4, paddingBottom: 8 }}>{children}</div>
    </div>
  );
}

export default function StockSettingsTab() {
  const push = useAdminToast();
  const qc = useQueryClient();
  const invalidate = useInvalidateStockConfig();
  const { data, isLoading, isError, error } = useStockConfig();
  const [labels, setLabels] = useState<StockTagLabels>({ ok: '', partial: '', short: '' });
  const [copies, setCopies] = useState('1');

  useEffect(() => {
    if (!data) return;
    setLabels(data.tagLabels);
    setCopies(String(data.printCopies));
  }, [data]);

  async function save(patch: Partial<StockConfig>, okMsg = 'Ustawienia Magazynu zapisane') {
    const prev = qc.getQueryData<StockConfig>(STOCK_KEYS.config);
    if (prev) qc.setQueryData<StockConfig>(STOCK_KEYS.config, { ...prev, ...patch });
    try {
      const r = await saveStockConfig(patch);
      qc.setQueryData(STOCK_KEYS.config, r);
      invalidate();
      push(okMsg);
    } catch (e) {
      if (prev) qc.setQueryData(STOCK_KEYS.config, prev);
      push(stockErrorText(e, 'Nie udało się zapisać ustawień'), 'err');
    }
  }

  if (isLoading) return <PageSkeleton />;
  if (isError || !data) {
    return (
      <div className="card"><div className="empty">
        <div className="ei"><Icon name="warehouse" size={26} /></div>
        <h3>Magazyn niedostępny</h3>
        <p>{stockErrorText(error, 'Ta wersja serwera nie obsługuje modułu Magazyn.')}</p>
      </div></div>
    );
  }

  const c = data;
  const off = !c.enabled;

  function saveLabel(key: keyof StockTagLabels) {
    const v = labels[key].trim().slice(0, 40);
    if (!v) { setLabels(c.tagLabels); push('Etykieta nie może być pusta', 'err'); return; }
    if (v !== c.tagLabels[key]) void save({ tagLabels: { ...c.tagLabels, [key]: v } }, 'Etykieta znacznika zapisana');
  }
  function saveCopies() {
    const n = Math.round(Number(copies));
    if (!Number.isFinite(n) || n < 1 || n > 5) { push('Liczba kopii: od 1 do 5', 'err'); setCopies(String(c.printCopies)); return; }
    if (n !== c.printCopies) void save({ printCopies: n });
  }

  return (
    <div className="grid mga-settings" style={{ gap: 16 }}>
      {/* Moduł */}
      <div className={`card mga-modcard ${c.enabled ? 'on' : ''}`}>
        <div className="mga-modcard-ic"><Icon name="warehouse" size={24} /></div>
        <div className="meta">
          <div className="t">Włącz Magazyn</div>
          <div className="d">
            Stan pieczywa przy stanowisku pakowania: rano wpisujesz dostawę, PackFlow od razu pokazuje, czego brakuje do zamówień,
            drukuje listę „Do dobrania”, a każde spakowane zamówienie samo zdejmuje produkty ze stanu.
          </div>
        </div>
        <Toggle on={c.enabled} onChange={(v) => save({ enabled: v }, v ? 'Magazyn włączony' : 'Magazyn wyłączony')} />
      </div>

      <div className="mga-settings-grid">
        <div className="grid" style={{ gap: 16, alignContent: 'start' }}>
          <Section icon="rotate-ccw" title="Reset dzienny" sub="Nowy dzień = czysty stan" dim={off}>
            <div className="set-row">
              <div className="meta">
                <div className="t">Codzienny reset stanu</div>
                <div className="d">O tej godzinie stan pieczywa zeruje się; pozycje oznaczone „bez resetu” zostają.</div>
              </div>
              <Toggle on={c.resetEnabled} onChange={(v) => save({ resetEnabled: v })} />
            </div>
            <div className="set-row" style={{ opacity: c.resetEnabled ? 1 : 0.5 }}>
              <div className="meta"><div className="t">Godzina resetu</div><div className="d">Czas polski. Domyślnie o północy.</div></div>
              <select className="select" value={c.resetTime} disabled={!c.resetEnabled} onChange={(e) => save({ resetTime: e.target.value })} aria-label="Godzina resetu">
                {RESET_TIMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </Section>

          <Section icon="layers" title="Liczenie" sub="Jak łączyć warianty i nowe produkty" dim={off}>
            <div className="set-row" style={{ display: 'block' }}>
              <div className="meta" style={{ marginBottom: 10 }}>
                <div className="t">Pozycje w magazynie</div>
                <div className="d">Domyślne dla wszystkich produktów — pojedyncze produkty zmienisz w Magazyn → Produkty.</div>
              </div>
              <Choice
                value={c.groupBy}
                onChange={(v) => {
                  if (!window.confirm('Zmiana sposobu liczenia: stan pozycji, których to dotyczy, zacznie się od zera — ilości trzeba będzie wpisać od nowa. Kontynuować?')) return;
                  void save({ groupBy: v });
                }}
                options={[
                  { id: 'variant', title: 'Każdy wariant osobno', desc: 'Chleb 700 g i 1 kg to dwie pozycje', icon: 'list-checks' },
                  { id: 'product', title: 'Warianty produktu razem', desc: 'Jedna pozycja na produkt, bez względu na wariant', icon: 'layers' },
                ]}
              />
            </div>
            <div className="set-row">
              <div className="meta">
                <div className="t">Nowe produkty z zamówień od razu w magazynie</div>
                <div className="d">Produkt, który pierwszy raz pojawi się w zamówieniu, sam trafia na listę śledzonych.</div>
              </div>
              <Toggle on={c.autoTrackNew} onChange={(v) => save({ autoTrackNew: v })} />
            </div>
          </Section>

          <Section icon="clipboard-list" title="Zapotrzebowanie" sub="Które zamówienia liczymy i jak dzielimy zapas" dim={off}>
            <div className="set-row" style={{ display: 'block' }}>
              <div className="meta" style={{ marginBottom: 10 }}><div className="t">Zakres zamówień</div></div>
              <Choice
                value={c.demandScope}
                onChange={(v) => save({ demandScope: v })}
                options={[
                  { id: 'now', title: 'Kolejka na teraz (jak w kiosku)', desc: 'Dzisiejsze i zaległe — to, co operator widzi do spakowania', icon: 'timer' },
                  { id: 'all', title: 'Wszystkie otwarte zamówienia', desc: 'Również z terminem dostawy w kolejnych dniach', icon: 'calendar-days' },
                ]}
              />
            </div>
            <div className="set-row">
              <div className="meta">
                <div className="t">Licz też zamówienia z nierozpoznaną dostawą</div>
                <div className="d">Ich produkty też trzeba będzie spakować, więc domyślnie wliczają się do zapotrzebowania.</div>
              </div>
              <Toggle on={c.includeUnidentified} onChange={(v) => save({ includeUnidentified: v })} />
            </div>
            <div className="set-row" style={{ display: 'block' }}>
              <div className="meta" style={{ marginBottom: 10 }}>
                <div className="t">Przydział zapasu do zamówień</div>
                <div className="d">Decyduje o znacznikach przy zamówieniach, gdy pieczywa nie starcza dla wszystkich.</div>
              </div>
              <Choice
                value={c.allocation}
                onChange={(v) => save({ allocation: v })}
                options={[
                  { id: 'whole', title: 'Najpierw kompletne zamówienia', desc: 'Najwięcej paczek gotowych do spakowania od ręki', icon: 'package-check' },
                  { id: 'greedy', title: 'Po kolei wg priorytetu', desc: 'Zapas idzie do zamówień w kolejności z kolejki', icon: 'arrow-up-down' },
                ]}
              />
            </div>
          </Section>
        </div>

        <div className="grid" style={{ gap: 16, alignContent: 'start' }}>
          <Section icon="tag" title="Znaczniki przy zamówieniach" sub="Kolorowa informacja na liście w kiosku i w panelu" dim={off}>
            <div className="set-row">
              <div className="meta"><div className="t">Pokazuj znaczniki</div><div className="d">Przy każdym zamówieniu z pieczywem z magazynu.</div></div>
              <Toggle on={c.showOrderTags} onChange={(v) => save({ showOrderTags: v })} />
            </div>
            <div className="mga-taglabels" style={{ opacity: c.showOrderTags ? 1 : 0.5 }}>
              {(([['ok', 'Wszystko jest'], ['partial', 'Część jest'], ['short', 'Nic nie ma']]) as [keyof StockTagLabels, string][]).map(([k, hint]) => (
                <div className="mga-taglabel" key={k}>
                  <label htmlFor={`mga-tag-${k}`}>{hint}</label>
                  <input
                    id={`mga-tag-${k}`}
                    className="input"
                    value={labels[k]}
                    maxLength={40}
                    disabled={!c.showOrderTags}
                    onChange={(e) => setLabels((l) => ({ ...l, [k]: e.target.value }))}
                    onBlur={() => saveLabel(k)}
                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  />
                  <TagPreview status={k} text={labels[k]} />
                </div>
              ))}
            </div>
            <div className="set-row">
              <div className="meta">
                <div className="t">Pytaj przed pakowaniem zamówienia bez pełnego zapasu</div>
                <div className="d">Operator potwierdza, że rozpoczyna zamówienie, którego pieczywa jeszcze nie ma.</div>
              </div>
              <Toggle on={c.warnOnStart} onChange={(v) => save({ warnOnStart: v })} />
            </div>
          </Section>

          <Section icon="zap" title="Szybkie wpisywanie" sub="Przyciski przy dostawie i rozliczeniu" dim={off}>
            <div className="set-row" style={{ display: 'block' }}>
              <div className="meta" style={{ marginBottom: 10 }}>
                <div className="t">Przyciski +N</div>
                <div className="d">Od 1 do {MAX_STEPS} wartości (1–999). Krok produktu z katalogu dochodzi sam.</div>
              </div>
              <ChipListEditor
                values={c.quickSteps.map(String)}
                max={MAX_STEPS}
                mono
                placeholder="np. 12"
                render={(v) => <>+{v}</>}
                validate={(raw, cur) => {
                  const n = Math.round(Number(raw));
                  if (!Number.isFinite(n) || n < 1 || n > 999) return { error: 'Wartość od 1 do 999' };
                  if (cur.indexOf(String(n)) >= 0) return { error: `+${n} już jest na liście` };
                  return { value: String(n) };
                }}
                onChange={(next) => {
                  if (next.length === 0) { push('Zostaw przynajmniej jeden przycisk', 'err'); return; }
                  const nums = next.map(Number).sort((a, b) => a - b);
                  void save({ quickSteps: nums });
                }}
              />
            </div>
            <div className="set-row">
              <div className="meta">
                <div className="t">Zaokrąglaj „Do dobrania” do kroku</div>
                <div className="d">Brakuje 4 bułek, a krok to 6 — na liście będzie 6 (pełne opakowanie).</div>
              </div>
              <Toggle on={c.roundShortageToStep} onChange={(v) => save({ roundShortageToStep: v })} />
            </div>
          </Section>

          <Section icon="package-minus" title="Zdejmowanie ze stanu" sub="Powody do wyboru przy ręcznym zdjęciu" dim={off}>
            <div className="set-row" style={{ display: 'block' }}>
              <div className="meta" style={{ marginBottom: 10 }}>
                <div className="t">Powody</div>
                <div className="d">Do {MAX_REASONS} pozycji, każda do {MAX_REASON_LEN} znaków. Widać je w historii ruchów.</div>
              </div>
              <ChipListEditor
                values={c.removalReasons}
                max={MAX_REASONS}
                placeholder="np. Uszkodzony"
                validate={(raw, cur) => {
                  const v = raw.replace(/\s+/g, ' ').trim();
                  if (v.length > MAX_REASON_LEN) return { error: `Najwyżej ${MAX_REASON_LEN} znaków` };
                  if (cur.some((x) => x.toLowerCase() === v.toLowerCase())) return { error: 'Taki powód już jest' };
                  return { value: v };
                }}
                onChange={(next) => {
                  if (next.length === 0) { push('Zostaw przynajmniej jeden powód', 'err'); return; }
                  void save({ removalReasons: next });
                }}
              />
            </div>
          </Section>

          <Section icon="printer" title="Wydruki" sub="Stan magazynu i lista „Do dobrania”" dim={off}>
            <div className="set-row">
              <div className="meta">
                <div className="t">Liczba kopii</div>
                <div className="d">Wydruki idą na drukarkę przypisaną do „Raporty” (Ustawienia → Drukarki).</div>
              </div>
              <input
                className="select"
                style={{ width: 72 }}
                type="number"
                min={1}
                max={5}
                value={copies}
                onChange={(e) => setCopies(e.target.value)}
                onBlur={saveCopies}
                onKeyDown={(e) => { if (e.key === 'Enter') saveCopies(); }}
                aria-label="Liczba kopii"
              />
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
